import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

interface SocialProjectThreshold {
  id: string;
  name: string;
  type: string;
  threshold: number;
  current_contributions: number;
  is_threshold_reached: boolean;
  days_since_last_action: number;
}

export const useSocialEngagementNotifications = () => {
  const { user, tenantId } = useAuth();
  const navigate = useNavigate();
  const [thresholds, setThresholds] = useState<SocialProjectThreshold[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (tenantId && user) {
      checkSocialThresholds();
    }
  }, [tenantId, user]);

  const checkSocialThresholds = async () => {
    if (!tenantId) return;

    try {
      setLoading(true);
      
      // Get enabled social projects for this tenant with contribution data
      const { data, error } = await supabase
        .from('tenant_social_projects')
        .select(`
          id,
          social_project_id,
          social_projects!inner (
            name,
            project_type
          )
        `)
        .eq('tenant_id', tenantId)
        .eq('is_enabled', true);

      if (error) throw error;

      const projectThresholds: SocialProjectThreshold[] = [];

      for (const project of data || []) {
        // Get total contributions for this project from this tenant's customers
        const { data: contributions } = await supabase
          .from('customer_social_contributions')
          .select(`
            contribution_count,
            last_contribution_at,
            customers!inner (
              tenant_id
            )
          `)
          .eq('social_project_id', project.social_project_id)
          .eq('customers.tenant_id', tenantId);

        const totalContributions = contributions?.reduce((sum, c) => sum + c.contribution_count, 0) || 0;
        
        // Calculate days since last contribution
        let daysSinceLastAction = 999;
        if (contributions && contributions.length > 0) {
          const lastContribution = contributions
            .filter(c => c.last_contribution_at)
            .sort((a, b) => new Date(b.last_contribution_at!).getTime() - new Date(a.last_contribution_at!).getTime())[0];
          
          if (lastContribution?.last_contribution_at) {
            const lastDate = new Date(lastContribution.last_contribution_at);
            const today = new Date();
            daysSinceLastAction = Math.floor((today.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24));
          }
        }

        // Define thresholds based on project type
        let threshold = 10;
        if (project.social_projects.project_type === 'trees') threshold = 10;
        if (project.social_projects.project_type === 'families') threshold = 5;
        if (project.social_projects.project_type === 'education') threshold = 20;

        const isThresholdReached = totalContributions >= threshold;

        projectThresholds.push({
          id: project.id,
          name: project.social_projects.name,
          type: project.social_projects.project_type,
          threshold,
          current_contributions: totalContributions,
          is_threshold_reached: isThresholdReached,
          days_since_last_action: daysSinceLastAction
        });
      }

      setThresholds(projectThresholds);

      // Show notifications for reached thresholds
      const reachedThresholds = projectThresholds.filter(p => p.is_threshold_reached);

      if (reachedThresholds.length > 0) {
        toast.success(
          `${reachedThresholds.length} Soziale Projekt${reachedThresholds.length > 1 ? 'e haben' : ' hat'} Meilensteine erreicht!`,
          {
            id: 'social-thresholds-reached',
            action: {
              label: 'Details ansehen',
              onClick: () => navigate('/admin/soziales-engagement')
            },
            duration: 8000
          }
        );
      }

    } catch (error) {
      console.error('Error checking social engagement thresholds:', error);
    } finally {
      setLoading(false);
    }
  };

  return {
    thresholds,
    loading,
    checkSocialThresholds,
    reachedCount: thresholds.filter(t => t.is_threshold_reached).length,
    inactiveCount: thresholds.filter(t => t.days_since_last_action > 30).length,
    totalActions: thresholds.length
  };
};